/**
 * TestlabRealDataBackendPage - Backend Worker Version
 * 
 * Använder Deno Deploy backend-worker istället för browser-worker
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Activity, ArrowLeft, Play, Square, FileText, AlertCircle, Database, Zap, CheckCircle2, XCircle, Server, Loader2, FileWarning, Clock, Download } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RealDataUploader } from '@/components/RealDataUploader';
import { TaxAnalysisRulesPanel } from '@/components/TaxAnalysisRulesPanel';
import { makeBatches } from '@/lib/utils/tokens';
import { MINI_MAX_INPUT_TOKENS, EST_TOKENS_PER_ITEM } from '@/modules/audit-full-analysis/config';
import { ParsedCSVData, normalizeHeaderName, normalizeSwedishDate, normalizeSwedishNumber } from '@/lib/csv-parser';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { formatStockholm, formatWithUTC, getTimeDebugInfo } from '@/lib/datetime';

export function TestlabRealDataBackendPage() {
  const [isRunning, setIsRunning] = useState(false);
  const [activeRevisions, setActiveRevisions] = useState<string[]>([]);
  const [visibleRevisions, setVisibleRevisions] = useState<any[]>([]); // Alla revisions visas i UI
  const [revisionHistory, setRevisionHistory] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [dummyReport, setDummyReport] = useState<string>('');
  const [currentReport, setCurrentReport] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [uploadedData, setUploadedData] = useState<any[] | null>(null);
  const [csvMeta, setCsvMeta] = useState<ParsedCSVData | null>(null);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [fileName, setFileName] = useState<string>('');
  const [lastError, setLastError] = useState<string>('');
  const [batchStats, setBatchStats] = useState<Record<string, Record<string, number>>>({});
  const [batchErrors, setBatchErrors] = useState<Array<{
    dbId: string;
    revisionId: string;
    batchId: string;
    error: string;
    attempts: number;
    state: string;
  }>>([]);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState<any>(null);
  const [isRunningManualTick, setIsRunningManualTick] = useState(false);
  const [workerUrl, setWorkerUrl] = useState<string>(
    localStorage.getItem('deno-worker-url') || 'https://auditai-worker.deno.dev'
  );
  
  // Save worker URL to localStorage when it changes
  useEffect(() => {
    if (workerUrl && workerUrl !== 'https://your-project.deno.dev') {
      localStorage.setItem('deno-worker-url', workerUrl);
    }
  }, [workerUrl]);
  
  // Lokal preflight preview state
  const [preflightStatus, setPreflightStatus] = useState<{
    dateCoverage: number;
    amountCoverage: number;
    eventCoverage: number;
    sampleTxns: any[];
    canStart: boolean;
    errors: string[];
  } | null>(null);
  
  // Företagskonfiguration
  const [companyName, setCompanyName] = useState<string>('');
  const [orgNumber, setOrgNumber] = useState<string>('');
  const [companySize, setCompanySize] = useState<'small' | 'large'>('small');
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [generatingReportId, setGeneratingReportId] = useState<string | null>(null);
  const [vatReportingPeriod, setVatReportingPeriod] = useState<'monthly' | 'quarterly'>('monthly');
  const [showRulesPanel, setShowRulesPanel] = useState(false);
  const [workerHealth, setWorkerHealth] = useState<{ healthy: boolean; message: string } | null>(null);
  const [showDebugTools, setShowDebugTools] = useState(false);
  const [boostJobs, setBoostJobs] = useState<Record<string, any>>({});
  const [isStartingBoost, setIsStartingBoost] = useState<string | null>(null);
  const [reportsAvailable, setReportsAvailable] = useState<Set<string>>(new Set());

  // Auth status tracking
  useEffect(() => {
    let mounted = true;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (mounted) setIsAuthenticated(!!user);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session?.user);
    });
    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  // Health check for Deno Worker
  useEffect(() => {
    const checkWorkerHealth = async () => {
      try {
        const response = await fetch(`${workerUrl}/health`, {
          method: 'GET',
        });
        
        if (response.ok) {
          const data = await response.json();
          setWorkerHealth({
            healthy: data.status === 'healthy',
            message: `Worker online (${data.version || 'unknown'}, ${data.environment || 'unknown'})`
          });
        } else {
          setWorkerHealth({
            healthy: false,
            message: `Worker responded with status ${response.status}`
          });
        }
      } catch (error) {
        setWorkerHealth({
          healthy: false,
          message: `Worker unreachable: ${error instanceof Error ? error.message : 'Unknown error'}`
        });
      }
    };

    checkWorkerHealth();
    const interval = setInterval(checkWorkerHealth, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, [workerUrl]);

  // Load activeRevisions from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('testlab-active-revisions');
    if (saved) {
      try {
        setActiveRevisions(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse saved active revisions:', e);
      }
    }
  }, []);

  // Save activeRevisions to localStorage whenever it changes
  useEffect(() => {
    if (activeRevisions.length > 0) {
      localStorage.setItem('testlab-active-revisions', JSON.stringify(activeRevisions));
    } else {
      localStorage.removeItem('testlab-active-revisions');
    }
  }, [activeRevisions]);

  // Fetch revision history (last 20 revisions)
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchHistory = async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      const { data: revisions } = await supabase
        .from('revisions')
        .select('id, status, created_at, updated_at, completed_at, total_batches, processed_batches, progress_percent, total_tokens_in, total_tokens_out, company_name, file_name, org_number, started_at')
        .eq('user_id', user.user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (revisions) {
        setRevisionHistory(revisions);
        
        // Visa alla revisions i UI (inklusive completed)
        setVisibleRevisions(revisions);
        
        // Endast processing/queued revisions pollas aktivt
        const processing = revisions.filter(r => r.status === 'processing' || r.status === 'queued').map(r => r.id);
        setActiveRevisions(processing);
        setIsRunning(processing.length > 0);
        
        // Fetch report availability for completed revisions
        const completedIds = revisions.filter(r => r.status === 'completed').map(r => r.id);
        if (completedIds.length > 0) {
          const { data: reports } = await supabase
            .from('revision_reports')
            .select('revision_id')
            .in('revision_id', completedIds);
          
          if (reports) {
            setReportsAvailable(new Set(reports.map(r => r.revision_id)));
          }
        }
      }
    };

    fetchHistory();
    const interval = setInterval(fetchHistory, 5000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Real-time subscriptions for revisions and batches
  useEffect(() => {
    if (!isAuthenticated) return;

    console.log('[Realtime] Setting up subscriptions...');

    const channel = supabase
      .channel('revision-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'revisions',
        },
        (payload) => {
          console.log('[Realtime] Revision update:', payload);
          
          // Refetch history on any revision change
          supabase.auth.getUser().then(({ data: user }) => {
            if (!user.user) return;
            
            supabase
              .from('revisions')
              .select('id, status, created_at, updated_at, completed_at, total_batches, processed_batches, progress_percent, total_tokens_in, total_tokens_out, company_name, started_at')
              .eq('user_id', user.user.id)
              .order('created_at', { ascending: false })
              .limit(20)
              .then(({ data }) => {
                if (data) {
                  setRevisionHistory(data);
                  setVisibleRevisions(data); // ✅ Uppdatera också synliga revisions!
                  
                  // Fetch report availability
                  const completedIds = data.filter(r => r.status === 'completed').map(r => r.id);
                  if (completedIds.length > 0) {
                    supabase
                      .from('revision_reports')
                      .select('revision_id')
                      .in('revision_id', completedIds)
                      .then(({ data: reports }) => {
                        if (reports) {
                          setReportsAvailable(new Set(reports.map(r => r.revision_id)));
                        }
                      });
                  }
                  
                  // Show toast for completed revisions
                  if (payload.eventType === 'UPDATE' && payload.new.status === 'completed') {
                    toast({
                      title: '✅ Revision färdig',
                      description: `Revision ${payload.new.id.slice(0, 8)} är nu klar!`,
                    });
                  }
                }
              });
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'batches',
        },
        (payload) => {
          console.log('[Realtime] Batch update:', payload);
          // Batch updates will trigger revision updates via triggers, so we just log here
        }
      )
      .subscribe();

    return () => {
      console.log('[Realtime] Unsubscribing...');
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated]);

  const handleDataReady = async (csvData: ParsedCSVData, mapping: Record<string, string>, uploadedFileName: string) => {
    console.log('📁 Data ready:', csvData.validRows, 'rows with mapping:', mapping, 'file:', uploadedFileName);
    setUploadedData(csvData.rows);
    setCsvMeta(csvData);
    setFieldMapping(mapping);
    setFileName(uploadedFileName);
    
    // Kör lokal preflight preview
    runLocalPreflight(csvData.rows, csvData.headers, mapping);
  };

  const runLocalPreflight = (data: any[], headers: string[], mapping: Record<string, string>) => {
    console.log('🔍 Running local preflight preview...', { rows: data.length, headers, mapping });
    
    if (!data || data.length === 0) {
      setPreflightStatus(null);
      return;
    }
    
    const sample = data.slice(0, Math.min(50, data.length));
    
    // Hämta mappade kolumnnamn (svenska nycklar!)
    const dateSrc = mapping['Datum'];
    const amountSrc = mapping['Belopp'];
    const eventSrc = mapping['Händelse'];
    
    const errors: string[] = [];
    if (!dateSrc) errors.push('Datum är inte mappat');
    if (!amountSrc) errors.push('Belopp är inte mappat');
    if (!eventSrc) errors.push('Händelse är inte mappat');
    
    // Räkna coverage med normalisering
    let dateCount = 0;
    let amountCount = 0;
    let eventCount = 0;
    
    sample.forEach(row => {
      if (dateSrc) {
        const dateVal = normalizeSwedishDate(String(row[dateSrc] ?? ''));
        if (dateVal) dateCount++;
      }
      if (amountSrc) {
        const amountVal = normalizeSwedishNumber(String(row[amountSrc] ?? ''));
        if (amountVal !== null) amountCount++;
      }
      if (eventSrc) {
        const eventVal = String(row[eventSrc] ?? '').trim();
        if (eventVal) eventCount++;
      }
    });
    
    const dateCoverage = sample.length ? (dateCount / sample.length) * 100 : 0;
    const amountCoverage = sample.length ? (amountCount / sample.length) * 100 : 0;
    const eventCoverage = sample.length ? (eventCount / sample.length) * 100 : 0;
    
    // Lägg till coverage-fel om för låg täckning
    if (sample.length > 0) {
      if (dateCoverage < 80) errors.push('Datumfältet täcker mindre än 80% av raderna');
      if (amountCoverage < 80) errors.push('Beloppsfältet täcker mindre än 80% av raderna');
    }
    
    const canStart = errors.length === 0;
    
    setPreflightStatus({
      dateCoverage,
      amountCoverage,
      eventCoverage,
      sampleTxns: sample.slice(0, 5),
      canStart,
      errors
    });
    
    console.log('✅ Preflight done:', { dateCoverage, amountCoverage, eventCoverage, canStart, errors });
  };

  const startBackendWorkerTest = async () => {
    console.log('🔥 START: startBackendWorkerTest called');
    setLastError('');
    
    // Validera lokal preflight
    if (preflightStatus && !preflightStatus.canStart) {
      toast({
        title: 'Kan inte starta analysen',
        description: 'Mappningen uppfyller inte kvalitetskrav.',
        variant: 'destructive',
      });
      return;
    }
    
    if (!uploadedData || !fieldMapping) {
      setLastError('Ingen data uppladdad');
      toast({
        title: 'Ingen data uppladdad',
        description: 'Ladda upp CSV-fil och konfigurera fältmappning först',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        toast({
          title: 'Autentisering krävs',
          description: 'Du måste vara inloggad för att köra analysen.',
          variant: 'destructive',
        });
        return;
      }

      // Förhindra dubbstarter
      const { data: existingActive } = await supabase
        .from('revisions')
        .select('id')
        .eq('user_id', user.id)
        .not('status', 'in', '(completed,cancelled)')
        .limit(1);
      
      if ((existingActive || []).length > 0) {
        toast({
          title: 'Analys redan igång',
          description: 'Det finns redan en aktiv analys.',
          variant: 'destructive',
        });
        return;
      }

      setIsRunning(true);
      console.log('🚀 Starting backend worker analysis...');
      
      // Anropa edge function för att starta backend worker
      const { data, error } = await supabase.functions.invoke('start-backend-worker', {
        body: {
          data: uploadedData,
          fieldMapping,
          companySize,
          vatReportingPeriod,
          fileName,
          companyName,
          orgNumber,
        }
      });

      if (error) throw error;

      console.log('✅ Backend worker started:', data);
      setActiveRevisions([data.revision_id]);
      
      const actualBatchCount = makeBatches(uploadedData, MINI_MAX_INPUT_TOKENS, EST_TOKENS_PER_ITEM).length;
      toast({
        title: 'Backend-analys startad',
        description: `Analyserar ${uploadedData.length} rader med Deno Deploy worker (${actualBatchCount} batcher)`,
      });
      
      // Starta auto-processing automatiskt
      try {
        await startAutoProcessing();
      } catch (autoError: any) {
        console.error('⚠️ Auto-processing kunde inte startas automatiskt:', autoError);
        toast({
          title: '⚠️ Auto-processing misslyckades',
          description: 'Du kan starta det manuellt med knappen nedan',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('❌ Backend worker start error:', error);
      setLastError(error?.message || 'Ett okänt fel uppstod');
      toast({
        title: 'Kunde inte starta analysen',
        description: error?.message || 'Ett okänt fel uppstod',
        variant: 'destructive',
      });
      setIsRunning(false);
    }
  };

  const regenerateReport = async (revisionId: string) => {
    setIsGeneratingReport(true);
    setGeneratingReportId(revisionId);
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-revision-report', {
        body: { revisionId }
      });
      
      if (error) throw error;
      
      toast({
        title: '✅ Rapport regenererad!',
        description: 'Rapporten har genererats med förbättrade prompter',
      });
      
      // Revision history will refresh automatically via polling
    } catch (err: any) {
      console.error('Regenerate report error:', err);
      toast({
        title: '❌ Kunde inte regenerera rapport',
        description: err.message || 'Ett okänt fel uppstod',
        variant: 'destructive'
      });
    } finally {
      setIsGeneratingReport(false);
      setGeneratingReportId(null);
    }
  };

  const viewReport = async (revisionId: string) => {
    try {
      // Fetch report
      const { data: report, error: reportError } = await supabase
        .from('revision_reports')
        .select('*')
        .eq('revision_id', revisionId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (reportError || !report) {
        // Fetch batch diagnostics to understand why report is missing
        const { data: batches } = await supabase
          .from('batches')
          .select('id, batch_no, state, tokens_in, tokens_out, last_error')
          .eq('revision_id', revisionId)
          .order('batch_no');

        const totalTokens = batches?.reduce((sum, b) => sum + (b.tokens_in || 0) + (b.tokens_out || 0), 0) || 0;
        const hasErrors = batches?.some(b => b.last_error) || false;
        
        let message = 'Rapporten kunde inte hittas.';
        if (totalTokens === 0) {
          message = '⚠️ AI-analys kördes inte (0 tokens). Batchen blev "completed" utan AI-anrop. Kör om analysen med den nya forceAI-koden.';
        } else if (hasErrors) {
          message = `Batchen hade fel: ${batches?.find(b => b.last_error)?.last_error || 'Okänt fel'}`;
        } else if (totalTokens > 0) {
          // Tokens exist but report missing - prompt to generate
          message = 'Rapporten finns inte ännu. Klicka "Generera rapport" för att skapa den.';
        }

        toast({
          title: 'Rapport ej tillgänglig',
          description: message,
          variant: 'destructive',
        });
        
        // Log diagnostics
        console.log('[viewReport] Diagnostics:', { revisionId, batches, totalTokens, hasErrors });
        return;
      }

      if (report.report_html) {
        setDummyReport(report.report_html);
        setCurrentReport(report);
        toast({ 
          title: 'Rapport hämtad',
          description: `${report.total_findings || 0} fynd (${report.critical_findings || 0} kritiska)`
        });
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast({
        title: 'Fel',
        description: 'Ett fel uppstod vid hämtning av rapporten',
        variant: 'destructive',
      });
    }
  };

  const generateReport = async (revisionId: string) => {
    try {
      setIsGeneratingReport(true);
      setGeneratingReportId(revisionId);
      toast({ title: 'Genererar rapport...', description: 'Detta kan ta en stund' });

      const { data, error } = await supabase.functions.invoke('generate-revision-report', {
        body: { revisionId }
      });

      if (error) {
        toast({
          title: 'Kunde inte generera rapport',
          description: error.message,
          variant: 'destructive'
        });
        return;
      }

      toast({ 
        title: 'Rapport genererad!', 
        description: `${data.total_findings} fynd (${data.critical_findings} kritiska)`
      });
      
      // Refresh history to show new report
      const { data: user } = await supabase.auth.getUser();
      if (user.user) {
        const { data: revisions } = await supabase
          .from('revisions')
          .select('id, status, created_at, updated_at, total_batches, processed_batches, progress_percent, total_tokens_in, total_tokens_out, total_cost_sek')
          .eq('user_id', user.user.id)
          .order('created_at', { ascending: false })
          .limit(20);
        
        if (revisions) {
          setRevisionHistory(revisions);
        }
      }
    } catch (err) {
      console.error('Error generating report:', err);
      toast({
        title: 'Fel',
        description: 'Ett fel uppstod vid rapportgenerering',
        variant: 'destructive'
      });
    } finally {
      setIsGeneratingReport(false);
      setGeneratingReportId(null);
    }
  };

  const clearActiveTests = async () => {
    // Proceed to force-clear regardless of local state; the edge function will determine actual active analyses.
    console.log('[clearActiveTests] Invoking force-clear-revisions');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast({
          title: "Autentisering krävs",
          description: "Du måste vara inloggad för att rensa analyser.",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('force-clear-revisions', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      toast({
        title: "✅ Analyser rensade",
        description: `${data.cleared} aktiva analyser har stoppats och rensats.`,
      });

      // Återställ state
      setActiveRevisions([]);
      setStats(null);
      setBatchStats({});
      setIsRunning(false);

      console.log('[clearActiveTests] Cleared revisions:', data.revisions);
    } catch (err: any) {
      console.error('[clearActiveTests] Error:', err);
      toast({
        title: "❌ Fel vid rensning",
        description: err.message || 'Kunde inte rensa analyser',
        variant: "destructive",
      });
    }
  };

  async function startBoostAll(revisionId: string) {
    if (!isAuthenticated) {
      toast({
        title: 'Ej autentiserad',
        description: 'Du måste vara inloggad för att starta boost',
        variant: 'destructive',
      });
      return;
    }

    setIsStartingBoost(revisionId);

    try {
      const { data, error } = await supabase.functions.invoke('boost-all-findings', {
        body: { revisionId }
      });

      if (error) throw error;

      toast({
        title: 'Boost All startad',
        description: `${data.totalFindings} findings kommer att boostas (~${data.estimatedMinutes} min)`,
      });
    } catch (err: any) {
      console.error('Failed to start boost:', err);
      toast({
        title: 'Boost misslyckades',
        description: err.message || 'Okänt fel',
        variant: 'destructive',
      });
    } finally {
      setIsStartingBoost(null);
    }
  }

  const pingWorker = async () => {
    console.log('[pingWorker] Pinging Deno Deploy worker...');
    
    try {
      const response = await fetch(`${workerUrl}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      toast({
        title: '✅ Worker Online',
        description: `Status: ${data.status} | Environment: ${data.environment}`,
      });
      
      return data;
    } catch (error: any) {
      console.error('[pingWorker] Error:', error);
      toast({
        title: '❌ Worker Offline',
        description: error.message || 'Kunde inte nå Deno Deploy worker',
        variant: 'destructive',
      });
      throw error;
    }
  };
  
  const tickWorker = async () => {
    console.log('[tickWorker] Triggering worker /tick endpoint...');
    
    try {
      const response = await fetch(`${workerUrl}/tick`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      toast({
        title: data.success ? '✅ Worker Tick' : '❌ Worker Tick Failed',
        description: data.message,
      });
      
      return data;
    } catch (error: any) {
      console.error('[tickWorker] Error:', error);
      toast({
        title: '❌ Worker Tick Failed',
        description: error.message || 'Kunde inte trigga worker',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const runDiagnostics = async () => {
    console.log('[runDiagnostics] Starting diagnostics...');
    setDiagnosticData(null);
    
    try {
      // 1. Test Deno Deploy worker health
      let workerHealth = null;
      let workerError = null;
      try {
        workerHealth = await pingWorker();
      } catch (err: any) {
        workerError = err.message;
      }
      
      // 2. Get queue health (READ-ONLY - no batch reservation)
      const { data: queueData, error: queueErr } = await supabase
        .from('batches')
        .select('state, created_at, updated_at, batch_id, attempts')
        .in('revision_id', activeRevisions)
        .order('created_at', { ascending: true });

      const queueHealth = queueData ? {
        total: queueData.length,
        byState: queueData.reduce((acc: any, b: any) => {
          acc[b.state] = (acc[b.state] || 0) + 1;
          return acc;
        }, {}),
        oldestQueued: queueData.find((b: any) => b.state === 'queued')?.created_at,
        oldestProcessing: queueData.find((b: any) => b.state === 'processing')?.created_at,
        stuckBatches: queueData.filter((b: any) => {
          if (b.state !== 'processing') return false;
          const age = Date.now() - new Date(b.updated_at).getTime();
          return age > 5 * 60 * 1000; // > 5 minuter
        }).length,
        lastUpdated: queueData[0]?.updated_at
      } : null;

      setDiagnosticData({
        workerHealth,
        workerError,
        queueHealth,
        queueError: queueErr?.message,
        timestamp: new Date().toISOString()
      });
      
      setShowDiagnostics(true);
    } catch (error) {
      console.error('[runDiagnostics] Error:', error);
      toast({
        title: 'Diagnostik misslyckades',
        description: 'Kunde inte köra diagnostik',
        variant: 'destructive'
      });
    }
  };

  const runManualTick = async () => {
    console.log('[runManualTick] Running manual tick...');
    setIsRunningManualTick(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('worker-manual-tick');
      if (error) throw error;
      
      console.log('[runManualTick] Result:', data);
      if (data?.success) {
        toast({
          title: '✅ Manual tick lyckades',
          description: `Batch ${data.batch_id} processad (${data.findings_count} fynd, ${data.tokens_used} tokens)`
        });
        // Stats will auto-update via polling
      } else {
        toast({
          title: '⚠️ Manual tick misslyckades',
          description: data?.reason || 'unknown',
          variant: 'destructive'
        });
      }
    } catch (error: any) {
      console.error('[runManualTick] Error:', error);
      toast({
        title: '❌ Fel vid manual tick',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsRunningManualTick(false);
    }
  };
  
  const startAutoProcessing = async () => {
    console.log('[startAutoProcessing] ======= START =======');
    console.log('[startAutoProcessing] workerUrl:', workerUrl);
    console.log('[startAutoProcessing] Full URL:', `${workerUrl}/auto-process`);
    
    try {
      console.log('[startAutoProcessing] About to fetch...');
      
      const response = await fetch(`${workerUrl}/auto-process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors', // Explicit CORS mode
      });
      
      console.log('[startAutoProcessing] Fetch completed, status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[startAutoProcessing] Response not OK:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('[startAutoProcessing] Success data:', data);
      
      toast({
        title: '✅ Auto-processing startad (Deno Deploy)',
        description: 'Workern processar batches automatiskt'
      });
      
      return data;
      
    } catch (denoError: any) {
      console.error('[startAutoProcessing] ======= DENO DEPLOY ERROR =======');
      console.error('[startAutoProcessing] Error type:', denoError.constructor.name);
      console.error('[startAutoProcessing] Error message:', denoError.message);
      console.error('[startAutoProcessing] Error stack:', denoError.stack);
      console.error('[startAutoProcessing] Full error:', denoError);
      
      console.warn('[startAutoProcessing] Deno Deploy misslyckades, använder fallback:', denoError.message);
      
      // FALLBACK: Använd Supabase Edge Function loop istället
      toast({
        title: '⚠️ Använder fallback-metod',
        description: 'Kör auto-processing via Supabase Edge Function',
        variant: 'default'
      });
      
      // Starta en bakgrundsloop som anropar worker-manual-tick upprepade gånger
      const processLoop = async () => {
        let consecutiveErrors = 0;
        let totalProcessed = 0;
        
        console.log('[processLoop] Startar fallback-loop...');
        
        while (consecutiveErrors < 3) {
          try {
            console.log(`[processLoop] Tick #${totalProcessed + 1}, försöker processa nästa batch...`);
            
            const { data, error } = await supabase.functions.invoke('worker-manual-tick');
            
            if (error) {
              console.error('[processLoop] Supabase invoke error:', error);
              consecutiveErrors++;
              await new Promise(resolve => setTimeout(resolve, 2000));
              continue;
            }
            
            if (!data?.success) {
              console.warn(`[processLoop] Tick ${consecutiveErrors + 1}/3 misslyckades:`, data?.reason);
              
              // Om inga batches finns kvar, är vi klara
              if (data?.reason === 'no_batches_available' || data?.reason === 'no_active_revisions') {
                console.log('[processLoop] Alla batches färdiga!');
                toast({
                  title: '✅ Auto-processing klar',
                  description: `${totalProcessed} batches processade via fallback-metod`
                });
                break;
              }
              
              consecutiveErrors++;
              await new Promise(resolve => setTimeout(resolve, 2000));
              continue;
            }
            
            consecutiveErrors = 0; // Reset på success
            totalProcessed++;
            console.log(`[processLoop] ✅ Batch ${data.batch_id} processed (${totalProcessed} totalt)`);
            
            // Vänta 2 sekunder innan nästa batch
            await new Promise(resolve => setTimeout(resolve, 2000));
            
          } catch (loopError: any) {
            console.error('[processLoop] Exception:', loopError);
            consecutiveErrors++;
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
        
        if (consecutiveErrors >= 3) {
          console.error('[processLoop] För många fel i rad, avbryter loop');
          toast({
            title: '❌ Auto-processing avbröts',
            description: 'För många fel uppstod i fallback-metoden',
            variant: 'destructive'
          });
        }
        
        console.log('[processLoop] Loop avslutad');
      };
      
      // Starta loopen i bakgrunden (utan await)
      processLoop().catch(err => {
        console.error('[processLoop] Fatal error:', err);
        toast({
          title: '❌ Auto-processing avbröts',
          description: err.message,
          variant: 'destructive'
        });
      });
      
      return { success: true, method: 'fallback', message: 'Använder fallback-metod' };
    }
  };

  const resetStuckBatches = async () => {
    if (!activeRevisions.length) {
      toast({
        title: '⚠️ Inga aktiva revisioner',
        description: 'Inga aktiva revisioner att återställa',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      const { data, error } = await supabase.functions.invoke('cleanup-stuck-batches');
      
      if (error) throw error;
      
      toast({
        title: '✅ Återställning klar',
        description: `Återställde ${data.cleaned || 0} fastnade batchar`
      });
      
      // Uppdatera statistik direkt
      runDiagnostics();
    } catch (error: any) {
      console.error('Reset error:', error);
      toast({
        title: '❌ Återställning misslyckades',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const clearBatchError = async (dbId: string, state: string) => {
    try {
      // Om batchen är completed, rensa bara last_error (städa gamla spökfel)
      // Annars återställ till queued (retry fel)
      if (state === 'completed') {
        const { error } = await supabase
          .from('batches')
          .update({
            last_error: null,
            updated_at: new Date().toISOString()
          })
          .eq('id', dbId);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('batches')
          .update({
            state: 'queued',
            last_error: null,
            attempts: 0,
            updated_at: new Date().toISOString()
          })
          .eq('id', dbId);
        
        if (error) throw error;
      }
      
      toast({
        title: '✅ Batch-fel rensat',
        description: state === 'completed' ? 'Gamla fel städade' : 'Batch återställd till kö',
      });
      
      // Uppdatera batch-statistiken direkt (bara aktiva revisioner)
      const pollBatches = async () => {
        if (activeRevisions.length === 0) {
          setBatchErrors([]);
          return;
        }
        
        const { data: batches } = await supabase
          .from('batches')
          .select('id, revision_id, state, batch_id, last_error, attempts')
          .in('revision_id', activeRevisions);
        
        if (batches) {
          const batchStatsByRevision = batches.reduce((acc, batch) => {
            if (!acc[batch.revision_id]) {
              acc[batch.revision_id] = { queued: 0, processing: 0, completed: 0, failed: 0, retry: 0 };
            }
            const state = batch.state as keyof typeof acc[typeof batch.revision_id];
            if (acc[batch.revision_id][state] !== undefined) {
              acc[batch.revision_id][state]++;
            }
            return acc;
          }, {} as Record<string, Record<string, number>>);
          
          setBatchStats(batchStatsByRevision);
          
          const errorBatches = batches
            .filter((b: any) => b.last_error && b.state !== 'completed')
            .map((b: any) => ({
              dbId: b.id,
              revisionId: b.revision_id,
              batchId: b.batch_id,
              error: b.last_error,
              attempts: b.attempts || 0,
              state: b.state
            }));
          
          setBatchErrors(errorBatches);
        }
      };
      
      await pollBatches();
    } catch (error: any) {
      console.error('[clearBatchError] Error:', error);
      toast({
        title: '❌ Kunde inte rensa batch-fel',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  // Realtime stats polling - pollar bara aktiva revisions men behåller stats
  useEffect(() => {
    if (activeRevisions.length === 0) {
      // Om inga aktiva revisions, rensa revisions-listan helt
      if (stats) {
        setStats({ ...stats, active_revisions: 0, revisions: [] });
      }
      return;
    }

    const pollStats = async () => {
      try {
        const { data: revisions } = await supabase
          .from('revisions')
          .select('id, status, created_at, completed_at, total_cost_sek, total_tokens_in, total_tokens_out, company_name, started_at, processed_batches, total_batches, progress_percent')
          .in('id', activeRevisions);

        if (revisions) {
          const totalCost = revisions.reduce((sum, r) => sum + Number(r.total_cost_sek || 0), 0);
          const totalTokens = revisions.reduce((sum, r) => sum + Number(r.total_tokens_in || 0) + Number(r.total_tokens_out || 0), 0);

          setStats({
            active_revisions: revisions.length,
            total_cost: totalCost,
            total_tokens: totalTokens,
            revisions
          });

          // Check if any completed
          const completed = revisions.filter(r => r.status === 'completed');
          if (completed.length > 0 && completed.length === revisions.length) {
            setIsRunning(false);
            toast({
              title: 'Analys klar!',
              description: `${completed.length} ${completed.length === 1 ? 'revision' : 'revisioner'} slutförd`,
            });
          }
        }
      } catch (error) {
        console.error('Error polling stats:', error);
      }
    };

    pollStats();
    const interval = setInterval(pollStats, 3000);
    return () => clearInterval(interval);
  }, [activeRevisions]);

  // Batch stats polling - inkluderar både aktiva OCH nyligen completed revisions
  useEffect(() => {
    // Poll både processing/queued OCH nyligen completed revisions (< 5 min gamla)
    const recentRevisions = visibleRevisions
      .filter(r => {
        // Inkludera processing/queued
        if (r.status === 'processing' || r.status === 'queued') return true;
        
        // Inkludera completed om de är < 5 minuter gamla
        if (r.status === 'completed' && r.completed_at) {
          const completedTime = new Date(r.completed_at).getTime();
          const now = Date.now();
          const fiveMinutes = 5 * 60 * 1000;
          return (now - completedTime) < fiveMinutes;
        }
        
        return false;
      })
      .map(r => r.id);

    if (recentRevisions.length === 0) {
      // Töm endast errors, BEHÅLL batchStats för visning
      setBatchErrors([]);
      return;
    }

    const pollBatches = async () => {
      try {
        const { data: batches } = await supabase
          .from('batches')
          .select('id, revision_id, state, batch_id, last_error, attempts')
          .in('revision_id', recentRevisions);

        if (batches) {
          // Gruppera per revision och state
          const batchStatsByRevision = batches.reduce((acc, batch) => {
            if (!acc[batch.revision_id]) {
              acc[batch.revision_id] = { queued: 0, processing: 0, completed: 0, failed: 0, retry: 0 };
            }
            const state = batch.state as keyof typeof acc[typeof batch.revision_id];
            if (acc[batch.revision_id][state] !== undefined) {
              acc[batch.revision_id][state]++;
            }
            return acc;
          }, {} as Record<string, Record<string, number>>);

          setBatchStats(batchStatsByRevision);

          // Extract batches with errors (exkludera completed)
          const errorBatches = batches
            .filter((b: any) => b.last_error && b.state !== 'completed')
            .map((b: any) => ({
              dbId: b.id,
              revisionId: b.revision_id,
              batchId: b.batch_id,
              error: b.last_error,
              attempts: b.attempts || 0,
              state: b.state
            }));

          setBatchErrors(errorBatches);
        }
      } catch (error) {
        console.error('Error polling batch stats:', error);
      }
    };

    pollBatches();
    const interval = setInterval(pollBatches, 3000);
    return () => clearInterval(interval);
  }, [visibleRevisions]);

  return (
    <div className="container max-w-7xl py-6 space-y-6">
      {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/testlab">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tillbaka till Testlab
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <Server className="h-8 w-8 text-purple-500" />
                Real Data (Backend Worker)
              </h1>
              <p className="text-muted-foreground mt-1">
                Fullständig AI-analys med Deno Deploy backend-worker
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={runDiagnostics}
              variant="outline"
              size="sm"
              className="gap-2"
              disabled={!isAuthenticated}
            >
              <Activity className="h-4 w-4" />
              Diagnostik
            </Button>
            
            <Button
              onClick={() => setShowDebugTools(!showDebugTools)}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <Activity className="h-4 w-4" />
              {showDebugTools ? 'Göm' : 'Visa'} Debug-verktyg
            </Button>
            
            {(activeRevisions.length > 0 || (stats?.active_revisions ?? 0) > 0) && (
              <>
                <Button
                  onClick={startAutoProcessing}
                  variant="default"
                  size="sm"
                  className="gap-2"
                  disabled={!isAuthenticated || isRunning}
                >
                  <Play className="h-4 w-4" />
                  Starta processning
                </Button>
                <Button
                  onClick={clearActiveTests}
                  variant="destructive"
                  size="sm"
                  className="gap-2"
                  disabled={!isAuthenticated}
                >
                  <Square className="h-4 w-4" />
                  Stoppa & Rensa
                </Button>
              </>
            )}
          </div>
        </div>

      {/* Worker Health Alert */}
      {workerHealth && !workerHealth.healthy && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Deno Worker ej nåbar</AlertTitle>
          <AlertDescription>
            {workerHealth.message}
            <br />
            <span className="text-xs">URL: {workerUrl}</span>
          </AlertDescription>
        </Alert>
      )}

      {/* Time Debug Panel */}
      {showDebugTools && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Clock className="h-5 w-5" />
              Tidsinformation (Debug)
            </CardTitle>
            <CardDescription>
              Server använder UTC i databasen. Lokal tid visas i Europe/Stockholm (CET/CEST).
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(() => {
              const debugInfo = getTimeDebugInfo();
              return (
                <div className="space-y-2 text-sm font-mono">
                  <div>
                    <strong>Server (ISO):</strong> {debugInfo.serverNow}
                  </div>
                  <div>
                    <strong>Lokal (Stockholm):</strong> {debugInfo.localStockholm}
                  </div>
                  <div>
                    <strong>Lokal (UTC):</strong> {debugInfo.localUTC}
                  </div>
                  <div>
                    <strong>Browser TZ:</strong> {debugInfo.browserTZ}
                  </div>
                  <div>
                    <strong>Browser Offset:</strong> UTC{debugInfo.browserOffset >= 0 ? '+' : ''}{debugInfo.browserOffset}h
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Debug Tools Section */}
      {showDebugTools && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-yellow-600" />
              Debug-verktyg (endast för felsökning)
            </CardTitle>
            <CardDescription>
              Dessa verktyg är deprecated och ska endast användas för manuell felsökning. Normal drift använder Deno Worker automatiskt.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Button
                onClick={tickWorker}
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={!isAuthenticated}
              >
                <Server className="h-4 w-4" />
                Ping Worker
              </Button>
              
              {(activeRevisions.length > 0 || (stats?.active_revisions ?? 0) > 0) && (
                <Button
                  onClick={runManualTick}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  disabled={!isAuthenticated || isRunningManualTick}
                >
                  <Play className="h-4 w-4" />
                  {isRunningManualTick ? 'Kör...' : 'Manual Tick (deprecated)'}
                </Button>
              )}
            </div>
            
            {workerHealth && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Worker Status:</h4>
                <div className={`p-3 rounded text-xs ${workerHealth.healthy ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <p className={workerHealth.healthy ? 'text-green-700' : 'text-red-700'}>
                    {workerHealth.healthy ? '✅' : '❌'} {workerHealth.message}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Diagnostics Panel */}
      {showDiagnostics && diagnosticData && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-orange-600" />
                Diagnostik - Worker Status
              </span>
              <div className="flex gap-2">
                <Button
                  onClick={runDiagnostics}
                  variant="outline"
                  size="sm"
                >
                  <Activity className="h-4 w-4 mr-2" />
                  Uppdatera
                </Button>
                <Button
                  onClick={resetStuckBatches}
                  variant="destructive"
                  size="sm"
                  disabled={!activeRevisions.length}
                >
                  <Activity className="h-4 w-4 mr-2" />
                  Reset Stuck Batches
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDiagnostics(false)}
                >
                  ✕
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Deno Deploy Worker Health */}
            <div>
              <h4 className="font-semibold mb-2">Deno Deploy Worker Health:</h4>
              {diagnosticData.workerHealth ? (
                <pre className="bg-green-50 border border-green-200 p-3 rounded text-xs overflow-auto max-h-48">
                  {JSON.stringify(diagnosticData.workerHealth, null, 2)}
                </pre>
              ) : (
                <div className="bg-red-50 border border-red-200 p-3 rounded">
                  <p className="text-red-600 text-sm">
                    ❌ Worker inte nåbar: {diagnosticData.workerError || 'Unknown error'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Kontrollera att Deno Deploy worker är deployad och URL:en är korrekt: {workerUrl}
                  </p>
                </div>
              )}
            </div>
          
            {/* Queue Health */}
            {diagnosticData.queueHealth && (
              <div>
                <h4 className="font-semibold mb-2">Köhälsa:</h4>
                <div className="bg-white p-3 rounded space-y-2">
                  <p><strong>Total batches:</strong> {diagnosticData.queueHealth.total}</p>
                  <p><strong>Status:</strong></p>
                  <ul className="ml-4">
                    {Object.entries(diagnosticData.queueHealth.byState).map(([state, count]: [string, any]) => (
                      <li key={state}>{state}: {count}</li>
                    ))}
                  </ul>
                  {diagnosticData.queueHealth.stuckBatches > 0 && (
                    <Alert variant="destructive" className="mt-2">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>⚠️ {diagnosticData.queueHealth.stuckBatches} batchar fastnade</strong> ({'>'} 5 min i processing)
                      </AlertDescription>
                    </Alert>
                  )}
                  {diagnosticData.queueHealth.oldestQueued && (
                    <p><strong>Äldsta köade:</strong> {formatWithUTC(diagnosticData.queueHealth.oldestQueued)}</p>
                  )}
                  {diagnosticData.queueHealth.oldestProcessing && (
                    <p><strong>Äldsta processing:</strong> {formatWithUTC(diagnosticData.queueHealth.oldestProcessing)}</p>
                  )}
                  {diagnosticData.queueHealth.lastUpdated && (
                    <p><strong>Senast uppdaterad:</strong> {formatWithUTC(diagnosticData.queueHealth.lastUpdated)}</p>
                  )}
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Timestamp: {formatWithUTC(diagnosticData.timestamp)}
            </p>
            
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-muted-foreground mb-2">
                ℹ️ Tider visas i Europe/Stockholm (CET/CEST); UTC visas inom parentes
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warning Banner */}
      <Alert className="border-purple-200 bg-purple-50">
        <Server className="h-4 w-4 text-purple-600" />
        <AlertDescription className="text-purple-900">
          <strong>Backend Worker Version</strong> - Denna version använder Deno Deploy server-worker istället av browser-worker.
          Workers körs 24/7 och kraschar inte vid browser-stängning.
          
          <div className="mt-3 flex items-center gap-2">
            <Label htmlFor="worker-url" className="text-sm font-medium">
              Deno Deploy URL:
            </Label>
            <input
              id="worker-url"
              type="text"
              value={workerUrl}
              onChange={(e) => setWorkerUrl(e.target.value)}
              placeholder="https://your-project.deno.dev"
              className="flex-1 px-3 py-1 text-sm border rounded-md"
            />
          </div>
        </AlertDescription>
      </Alert>

      {/* CSV Uploader */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            1. Ladda upp skattekontodata (CSV)
          </CardTitle>
          <CardDescription>
            Välj en CSV-fil med skattekontodata och konfigurera fältmappning
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* TODO: Testlab behöver egen uploader-implementation som inte använder storage */}
          <div className="p-8 border-2 border-dashed rounded-lg text-center text-muted-foreground">
            <p>RealDataUploader har migrer ats till storage-baserad arkitektur.</p>
            <p className="text-sm mt-2">Testlab kräver egen implementation för direktparsning.</p>
          </div>
          {/* <RealDataUploader 
            onDataReady={handleDataReady}
            customerId="backend-test"
            moduleName="skattekonto"
          /> */}
        </CardContent>
      </Card>

      {/* Preflight Preview */}
      {preflightStatus && (
        <Card>
          <CardHeader>
            <CardTitle>Preflight Preview</CardTitle>
            <CardDescription>Validering av fältmappning</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Datum Coverage</Label>
                <Progress value={preflightStatus.dateCoverage} className="mt-2" />
                <p className="text-sm text-muted-foreground mt-1">
                  {preflightStatus.dateCoverage.toFixed(1)}%
                </p>
              </div>
              <div>
                <Label>Belopp Coverage</Label>
                <Progress value={preflightStatus.amountCoverage} className="mt-2" />
                <p className="text-sm text-muted-foreground mt-1">
                  {preflightStatus.amountCoverage.toFixed(1)}%
                </p>
              </div>
              <div>
                <Label>Händelse Coverage</Label>
                <Progress value={preflightStatus.eventCoverage} className="mt-2" />
                <p className="text-sm text-muted-foreground mt-1">
                  {preflightStatus.eventCoverage.toFixed(1)}%
                </p>
              </div>
            </div>

            {preflightStatus.errors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {preflightStatus.errors.join('. ')}
                </AlertDescription>
              </Alert>
            )}

            {preflightStatus.canStart && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-900">
                  Mappningen ser bra ut! Du kan starta analysen.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Analysis Settings */}
      {uploadedData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              2. Företagskonfiguration & Start
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4 mb-4 pb-4 border-b">
              <div className="space-y-2">
                <Label htmlFor="company-name">Företagsnamn *</Label>
                <input
                  id="company-name"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="t.ex. Acme AB"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-number">Organisationsnummer *</Label>
                <input
                  id="org-number"
                  type="text"
                  value={orgNumber}
                  onChange={(e) => setOrgNumber(e.target.value)}
                  placeholder="t.ex. 556123-4567"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Företagsstorlek</Label>
                <div className="flex gap-2">
                  <Button
                    variant={companySize === 'small' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCompanySize('small')}
                  >
                    Litet (&lt; 3 anställda)
                  </Button>
                  <Button
                    variant={companySize === 'large' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCompanySize('large')}
                  >
                    Stort (≥ 3 anställda)
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Momsredovisningsperiod</Label>
                <div className="flex gap-2">
                  <Button
                    variant={vatReportingPeriod === 'monthly' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setVatReportingPeriod('monthly')}
                  >
                    Månadsvis
                  </Button>
                  <Button
                    variant={vatReportingPeriod === 'quarterly' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setVatReportingPeriod('quarterly')}
                  >
                    Kvartalsvis
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-4 border-t">
              <Button
                onClick={startBackendWorkerTest}
                disabled={!isAuthenticated || isRunning || !uploadedData || (preflightStatus && !preflightStatus.canStart) || !companyName.trim() || !orgNumber.trim()}
                className="gap-2"
              >
                <Play className="h-4 w-4" />
                Starta Backend-analys
              </Button>
              
              {(isRunning || activeRevisions.length > 0) && (
                <Button
                  onClick={startAutoProcessing}
                  variant="default"
                  size="sm"
                  className="gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                >
                  <Zap className="h-4 w-4" />
                  Start Auto-Processing
                </Button>
              )}

              {(isRunning || activeRevisions.length > 0) && (
                <Button
                  onClick={clearActiveTests}
                  variant="destructive"
                  size="sm"
                  className="gap-2"
                >
                  <Square className="h-4 w-4" />
                  Stoppa & Rensa
                </Button>
              )}

              <div className="flex items-center gap-2">
                <Switch
                  checked={showRulesPanel}
                  onCheckedChange={setShowRulesPanel}
                  id="show-rules"
                />
                <Label htmlFor="show-rules">Visa analysregler</Label>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analysis Rules Panel */}
      {showRulesPanel && (
        <TaxAnalysisRulesPanel
          companyConfig={{
            companySize,
            vatReportingPeriod
          }}
        />
      )}

      {/* Real-time Statistics - endast aktiva revisions */}
      {stats && stats.revisions && stats.revisions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Realtidsstatistik (Backend Worker)</CardTitle>
            <CardDescription>
              Live-data från Deno Deploy worker (${stats.active_revisions} aktiv${stats.active_revisions > 1 ? 'a' : ''})
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div>
                <Label>Aktiva Revisioner</Label>
                <p className="text-2xl font-bold">{stats.active_revisions}</p>
              </div>
              <div>
                <Label>Total Kostnad</Label>
                <p className="text-2xl font-bold">{stats.total_cost?.toFixed(2)} SEK</p>
              </div>
              <div>
                <Label>Total Tokens</Label>
                <p className="text-2xl font-bold">{stats.total_tokens?.toLocaleString()}</p>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <Label>Aktiva Revisioner</Label>
              {(() => {
                // Härledd lista: processing/queued + completed < 5 min gamla
                const recentRevisionsForUI = visibleRevisions.filter(r => {
                  if (r.status === 'processing' || r.status === 'queued') return true;
                  
                  // Inkludera completed om de är < 5 minuter gamla
                  const completedAt = r.completed_at || r.updated_at;
                  if (r.status === 'completed' && completedAt) {
                    const t = new Date(completedAt).getTime();
                    return (Date.now() - t) < 5 * 60 * 1000;
                  }
                  
                  return false;
                });
                
                return recentRevisionsForUI.map((rev: any) => {
                  const isCompleted = rev.status === 'completed';
                  
                  return (
                  <div key={rev.id} className={`p-4 border rounded space-y-3 ${isCompleted ? 'border-green-500 bg-green-50 dark:bg-green-950/20' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          {rev.file_name || rev.company_name || 'Backend Test'}
                          {isCompleted && <span className="ml-2 text-green-600 dark:text-green-400">✅ KLAR</span>}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {rev.started_at ? `Startad: ${formatWithUTC(rev.started_at)}` : `Skapad: ${formatWithUTC(rev.created_at)}`}
                        </p>
                        {isCompleted && rev.completed_at && (
                          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                            Slutförd: {formatWithUTC(rev.completed_at)}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={
                          rev.status === 'completed' ? 'success' :
                          rev.status === 'processing' ? 'secondary' :
                          rev.status === 'failed' ? 'destructive' : 'outline'
                        }>
                          {rev.status === 'completed' ? '✅ Completed' : 
                           rev.status === 'processing' ? '⏳ Processing' :
                           rev.status}
                        </Badge>
                      </div>
                    </div>

                    {/* Progress bar och batch-info */}
                    {rev.total_batches > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span>Batch Progress</span>
                          <span className="font-medium">
                            {rev.processed_batches || 0}/{rev.total_batches} 
                            ({(rev.progress_percent || 0).toFixed(1)}%)
                          </span>
                        </div>
                        <Progress value={rev.progress_percent || 0} className="h-2" />
                      </div>
                    )}

                    {/* Tokens */}
                    <div className="text-sm">
                      <span className="text-muted-foreground">Tokens:</span>
                      <span className="ml-2 font-medium">
                        {((Number(rev.total_tokens_in || 0) + Number(rev.total_tokens_out || 0)) * 10)
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                      </span>
                    </div>

                    {/* Active Batches - endast för processing revisions */}
                    {rev.status === 'processing' && batchStats[rev.id] && (
                      <div className="space-y-2">
                        <Label className="text-xs">Batch Status (Live)</Label>
                        <div className="grid grid-cols-4 gap-2">
                          <div className="text-center p-2 bg-blue-50 dark:bg-blue-950 rounded border border-blue-200 dark:border-blue-800">
                            <div className="font-semibold text-blue-600 dark:text-blue-400 text-lg">
                              {batchStats[rev.id].queued || 0}
                            </div>
                            <div className="text-xs text-muted-foreground">Köade</div>
                          </div>
                          <div className="text-center p-2 bg-yellow-50 dark:bg-yellow-950 rounded border border-yellow-200 dark:border-yellow-800">
                            <div className="font-semibold text-yellow-600 dark:text-yellow-400 text-lg">
                              {batchStats[rev.id].processing || 0}
                            </div>
                            <div className="text-xs text-muted-foreground">Processar</div>
                          </div>
                          <div className="text-center p-2 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800">
                            <div className="font-semibold text-green-600 dark:text-green-400 text-lg">
                              {batchStats[rev.id].completed || 0}
                            </div>
                            <div className="text-xs text-muted-foreground">Klara</div>
                          </div>
                          <div className="text-center p-2 bg-red-50 dark:bg-red-950 rounded border border-red-200 dark:border-red-800">
                            <div className="font-semibold text-red-600 dark:text-red-400 text-lg">
                              {batchStats[rev.id].failed || 0}
                            </div>
                            <div className="text-xs text-muted-foreground">Misslyckade</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Completed status info */}
                    {rev.status === 'completed' && (
                      <Alert className="border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800">
                        <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                        <AlertDescription className="text-green-800 dark:text-green-200">
                          Analysen är klar! {rev.total_batches > 0 && `${rev.total_batches} batch${rev.total_batches > 1 ? 'ar' : ''} analyserade.`}
                          {' '}Tryck "Generera rapport" för att skapa en sammanfattning.
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* View Report and Generate Report buttons */}
                    {rev.status === 'completed' && (
                      <TooltipProvider>
                        <div className="flex gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                size="sm" 
                                onClick={() => viewReport(rev.id)} 
                                className="flex-1"
                                variant={reportsAvailable.has(rev.id) ? "default" : "ghost"}
                                disabled={!reportsAvailable.has(rev.id)}
                              >
                                <FileText className="h-4 w-4 mr-2" />
                                Visa rapport
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {reportsAvailable.has(rev.id) 
                                ? "Öppna den genererade rapporten" 
                                : "Ingen rapport tillgänglig ännu - generera först"}
                            </TooltipContent>
                          </Tooltip>
                          {((rev.total_tokens_in || 0) + (rev.total_tokens_out || 0) > 0) && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => generateReport(rev.id)}
                                  disabled={generatingReportId === rev.id}
                                  className="flex-1"
                                >
                                  {generatingReportId === rev.id ? (
                                    <>
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      Genererar...
                                    </>
                                  ) : (
                                    <>
                                      <FileWarning className="h-4 w-4 mr-2" />
                                      Generera rapport
                                    </>
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Skapa en sammanfattande rapport från alla findings med AI
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TooltipProvider>
                    )}
                  </div>
                  );
                });
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stuck Batches Warning */}
      {diagnosticData?.queueHealth?.stuckBatches > 0 && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Varning: Fastnade batchar</AlertTitle>
          <AlertDescription>
            {diagnosticData.queueHealth.stuckBatches} batchar har varit i "processing" {'>'} 5 minuter.
            Klicka "Reset Stuck Batches" för att återställa dem.
          </AlertDescription>
        </Alert>
      )}

      {/* Batch Errors Panel */}
      {batchErrors.length > 0 && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                  <AlertCircle className="h-5 w-5" />
                  Batch-fel ({batchErrors.length})
                </CardTitle>
                <CardDescription>Batchar som misslyckades under processning</CardDescription>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    if (activeRevisions.length === 0) return;
                    
                    const { error } = await supabase
                      .from('batches')
                      .update({ 
                        last_error: null, 
                        updated_at: new Date().toISOString() 
                      })
                      .in('revision_id', activeRevisions)
                      .eq('state', 'completed')
                      .not('last_error', 'is', null);
                    
                    if (error) throw error;
                    
                    toast({
                      title: '✅ Alla gamla fel rensade',
                      description: 'Completed-batchar har städats',
                    });
                    
                    // Trigger omedelbar uppdatering
                    setBatchErrors([]);
                  } catch (error: any) {
                    toast({
                      title: '❌ Kunde inte rensa fel',
                      description: error.message,
                      variant: 'destructive'
                    });
                  }
                }}
                className="gap-2"
              >
                Rensa alla gamla fel
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {batchErrors.map((err) => (
              <div key={`${err.revisionId}:${err.batchId}`} className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-red-300 dark:border-red-800 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm font-semibold text-red-900 dark:text-red-300">
                    {err.batchId}
                  </span>
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive" className="text-xs">
                      Försök: {err.attempts}/5
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {err.state}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => clearBatchError(err.dbId, err.state)}
                      className="h-7 text-xs"
                    >
                      Rensa fel
                    </Button>
                  </div>
                </div>
                <pre className="text-xs text-red-600 dark:text-red-400 overflow-x-auto bg-red-50 dark:bg-red-950/50 p-2 rounded border border-red-200 dark:border-red-800">
                  {err.error}
                </pre>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Revision History */}
      <Card>
        <CardHeader>
          <CardTitle>📋 Revisionshistorik</CardTitle>
          <CardDescription>
            De senaste 20 revisionerna • Processar automatiskt i bakgrunden var 2:a minut
          </CardDescription>
        </CardHeader>
        <CardContent>
          {revisionHistory.length === 0 ? (
            <p className="text-muted-foreground">Ingen historik än. Starta en ny revision nedan.</p>
          ) : (
            <div className="space-y-2">
              {revisionHistory.map((rev) => (
                <div
                  key={rev.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium mb-1">
                      {(rev.file_name || rev.company_name || `Org: ${rev.org_number}`)} - {new Date(rev.created_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs">{rev.id.slice(0, 8)}</code>
                      <Badge variant={
                        rev.status === 'completed' ? 'default' :
                        rev.status === 'processing' ? 'secondary' :
                        rev.status === 'failed' ? 'destructive' : 'outline'
                      }>
                        {rev.status}
                      </Badge>
                      {rev.progress_percent > 0 && rev.status === 'processing' && (
                        <span className="text-sm text-muted-foreground">
                          {rev.progress_percent.toFixed(0)}% ({rev.processed_batches}/{rev.total_batches})
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Skapad: {formatWithUTC(rev.created_at)}
                      {rev.total_tokens_in + rev.total_tokens_out > 0 && (
                        <span className="ml-2">
                          • Tokens (Mini): {(rev.total_tokens_in + rev.total_tokens_out)
                            .toString()
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                          {((rev.premium_tokens_in || 0) + (rev.premium_tokens_out || 0)) > 0 && (
                            <> | Premium: {((rev.premium_tokens_in || 0) + (rev.premium_tokens_out || 0))
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}</>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  {rev.status === 'completed' && (
                    <TooltipProvider>
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                onClick={() => viewReport(rev.id)}
                                variant={reportsAvailable.has(rev.id) ? "default" : "ghost"}
                                disabled={!reportsAvailable.has(rev.id)}
                              >
                                Visa rapport
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {reportsAvailable.has(rev.id) 
                                ? "Öppna den genererade rapporten" 
                                : "Ingen rapport tillgänglig ännu - generera först"}
                            </TooltipContent>
                          </Tooltip>
                          {((rev.total_tokens_in || 0) + (rev.total_tokens_out || 0) > 0) && (
                            <>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => generateReport(rev.id)}
                                    disabled={generatingReportId === rev.id}
                                  >
                                    {generatingReportId === rev.id ? (
                                      <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Genererar...
                                      </>
                                    ) : (
                                      <>
                                        <FileWarning className="h-4 w-4 mr-2" />
                                        Generera
                                      </>
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Skapa en sammanfattande rapport från alla findings med AI
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => regenerateReport(rev.id)}
                                    disabled={generatingReportId === rev.id}
                                  >
                                    {generatingReportId === rev.id ? (
                                      <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Regenererar...
                                      </>
                                    ) : (
                                      <>
                                        <Zap className="h-4 w-4 mr-2" />
                                        Regenerera
                                      </>
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Kör rapportgenerering igen med de senaste förbättringarna
                                </TooltipContent>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      
                      {boostJobs[rev.id] && (
                        <div className="flex items-center gap-2 mt-1">
                          {boostJobs[rev.id].state === 'running' && (
                            <>
                              <Progress 
                                value={(boostJobs[rev.id].boosted_findings / boostJobs[rev.id].total_findings) * 100} 
                                className="flex-1"
                              />
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {boostJobs[rev.id].boosted_findings}/{boostJobs[rev.id].total_findings}
                                {boostJobs[rev.id].estimated_seconds && (() => {
                                  const progress = boostJobs[rev.id].boosted_findings / boostJobs[rev.id].total_findings;
                                  const remainingSeconds = Math.ceil(boostJobs[rev.id].estimated_seconds * (1 - progress));
                                  const remainingMinutes = Math.ceil(remainingSeconds / 60);
                                  return remainingMinutes > 0 ? ` (~${remainingMinutes} min kvar)` : '';
                                })()}
                              </span>
                            </>
                          )}
                          {boostJobs[rev.id].state === 'completed' && (
                            <Badge variant="default" className="text-xs">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              {boostJobs[rev.id].boosted_findings} boostade
                            </Badge>
                          )}
                          {boostJobs[rev.id].state === 'failed' && (
                            <Badge variant="destructive" className="text-xs">
                              <XCircle className="h-3 w-3 mr-1" />
                              Misslyckades
                            </Badge>
                          )}
                        </div>
                      )}
                      
                      {!boostJobs[rev.id] && (rev.total_tokens_in || 0) > 0 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startBoostAll(rev.id)}
                              disabled={isStartingBoost === rev.id}
                              className="w-fit"
                            >
                              {isStartingBoost === rev.id ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Startar...
                                </>
                              ) : (
                                <>
                                  <Zap className="h-4 w-4 mr-2" />
                                  Boost All Remaining
                                </>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Förbättra alla findings som inte redan har utökade beskrivningar
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    </TooltipProvider>
                  )}
                  {(rev.status === 'processing' || rev.status === 'queued') && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Activity className="h-4 w-4 animate-pulse" />
                      Processas i bakgrunden...
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Display */}
      {lastError && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{lastError}</AlertDescription>
        </Alert>
      )}

      {/* Report Display */}
      {dummyReport && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                Genererad rapport
              {currentReport && (() => {
                const reportJson = currentReport.report_json || {};
                const allFindings = reportJson.findings || [];
                const unboosted = allFindings.filter((f: any) => 
                  !f.details?.original_description && 
                  f.description && 
                  f.description.length < 120
                );
                
                if (unboosted.length > 10) {
                  return (
                    <Badge variant="outline" className="ml-2">
                      {unboosted.length} unboosted findings
                    </Badge>
                  );
                }
                return null;
              })()}
              </span>
              <Button
                onClick={async () => {
                  if (!currentReport) return;
                  try {
                    // Fetch org settings
                    const { data: orgSettings } = await supabase
                      .from('org_settings')
                      .select('*')
                      .maybeSingle();

                    // Generate PDF using jsPDF
                    const { default: jsPDF } = await import('jspdf');
                    const doc = new jsPDF('p', 'mm', 'a4');
                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();
                    const margin = 15;
                    
                    let yPos = margin;
                    
                    // Header
                    doc.setFontSize(24);
                    doc.setFont('helvetica', 'bold');
                    doc.text('SKATTEKONTOANALYS', margin, yPos);
                    yPos += 15;
                    
                    // Company info from report_json summary
                    const reportJson = currentReport.report_json as any;
                    const summary = reportJson?.summary || {};
                    
                    doc.setFontSize(12);
                    doc.setFont('helvetica', 'normal');
                    if (summary.company_name) {
                      doc.text(`Företag: ${summary.company_name}`, margin, yPos);
                      yPos += 7;
                    }
                    if (summary.org_number) {
                      doc.text(`Organisationsnummer: ${summary.org_number}`, margin, yPos);
                      yPos += 7;
                    }
                    
                    doc.setFontSize(10);
                    doc.setTextColor(100);
                    doc.text(`Genererad: ${new Date(currentReport.created_at).toLocaleDateString('sv-SE')}`, margin, yPos);
                    if (orgSettings?.firm_name) {
                      yPos += 5;
                      doc.text(`Byrå: ${orgSettings.firm_name}`, margin, yPos);
                    }
                    yPos += 12;
                    
                    // Statistics
                    doc.setFontSize(16);
                    doc.setTextColor(0);
                    doc.setFont('helvetica', 'bold');
                    doc.text('STATISTIK', margin, yPos);
                    yPos += 8;
                    
                    const findings = reportJson?.findings || [];
                    const criticalCount = findings.filter((f: any) => f.severity === 'critical').length;
                    const highCount = findings.filter((f: any) => f.severity === 'high').length;
                    const mediumCount = findings.filter((f: any) => f.severity === 'medium').length;
                    const lowCount = findings.filter((f: any) => f.severity === 'low').length;
                    
                    doc.setFontSize(11);
                    doc.setFont('helvetica', 'normal');
                    doc.text(`Totalt antal fynd: ${findings.length}`, margin, yPos);
                    yPos += 6;
                    
                    doc.setTextColor(220, 38, 38);
                    doc.text(`Kritiska: ${criticalCount}`, margin, yPos);
                    yPos += 6;
                    
                    doc.setTextColor(234, 88, 12);
                    doc.text(`Höga: ${highCount}`, margin, yPos);
                    yPos += 6;
                    
                    doc.setTextColor(245, 158, 11);
                    doc.text(`Medel: ${mediumCount}`, margin, yPos);
                    yPos += 6;
                    
                    doc.setTextColor(100);
                    doc.text(`Låga: ${lowCount}`, margin, yPos);
                    yPos += 12;
                    
                    // Executive Summary
                    if (currentReport.executive_summary) {
                      doc.setTextColor(0);
                      doc.setFontSize(16);
                      doc.setFont('helvetica', 'bold');
                      doc.text('SAMMANFATTNING', margin, yPos);
                      yPos += 8;
                      
                      doc.setFontSize(10);
                      
                      const summaryText = currentReport.executive_summary
                        .replace(/<[^>]+>/g, '')
                        .replace(/&nbsp;/g, ' ')
                        .replace(/&amp;/g, '&')
                        .replace(/&lt;/g, '<')
                        .replace(/&gt;/g, '>')
                        .replace(/&quot;/g, '"');
                      
                      // Split into paragraphs and identify section headers
                      const paragraphs = summaryText.split('\n').filter(p => p.trim());
                      const sectionHeaders = ['Övergripande bedömning:', 'Korrekt beteende:', 'Riskområden:', 'Rekommendation:'];
                      
                      paragraphs.forEach((para: string) => {
                        const trimmedPara = para.trim();
                        
                        // Check if this paragraph starts with a section header
                        const headerMatch = sectionHeaders.find(header => trimmedPara.startsWith(header));
                        
                        if (headerMatch) {
                          // Extract header and content
                          const header = headerMatch;
                          const content = trimmedPara.substring(header.length).trim();
                          
                          // Print header in bold
                          doc.setFont('helvetica', 'bold');
                          const headerLines = doc.splitTextToSize(header, pageWidth - 2 * margin);
                          headerLines.forEach((line: string) => {
                            if (yPos > pageHeight - margin) {
                              doc.addPage();
                              yPos = margin;
                            }
                            doc.text(line, margin, yPos);
                            yPos += 5;
                          });
                          
                          // Print content in normal style
                          doc.setFont('helvetica', 'normal');
                          if (content) {
                            const contentLines = doc.splitTextToSize(content, pageWidth - 2 * margin);
                            contentLines.forEach((line: string) => {
                              if (yPos > pageHeight - margin) {
                                doc.addPage();
                                yPos = margin;
                              }
                              doc.text(line, margin, yPos);
                              yPos += 5;
                            });
                          }
                        } else {
                          // Normal paragraph - regular style
                          doc.setFont('helvetica', 'normal');
                          const lines = doc.splitTextToSize(trimmedPara, pageWidth - 2 * margin);
                          lines.forEach((line: string) => {
                            if (yPos > pageHeight - margin) {
                              doc.addPage();
                              yPos = margin;
                            }
                            doc.text(line, margin, yPos);
                            yPos += 5;
                          });
                        }
                        
                        // Add small spacing between paragraphs
                        yPos += 2;
                      });
                      
                      yPos += 5;
                    }
                    
                    // Findings - always start on new page
                    if (findings.length > 0) {
                      doc.addPage();
                      yPos = margin;
                      
                      doc.setFontSize(16);
                      doc.setFont('helvetica', 'bold');
                      doc.setTextColor(0);
                      doc.text('DETALJERADE FYND', margin, yPos);
                      yPos += 10;
                      
                      findings.forEach((finding: any, index: number) => {
                        if (yPos > pageHeight - 40) {
                          doc.addPage();
                          yPos = margin;
                        }
                        
                        const severityColors: { [key: string]: [number, number, number] } = {
                          critical: [220, 38, 38],
                          high: [234, 88, 12],
                          medium: [245, 158, 11],
                          low: [100, 100, 100]
                        };
                        const color = severityColors[finding.severity] || [100, 100, 100];
                        
                        doc.setDrawColor(...color);
                        doc.setLineWidth(1);
                        doc.line(margin, yPos, margin, yPos + 30);
                        
                        doc.setFontSize(11);
                        doc.setFont('helvetica', 'bold');
                        doc.setTextColor(...color);
                        const severityText = finding.severity === 'critical' ? 'KRITISK' :
                                            finding.severity === 'high' ? 'HÖG' :
                                            finding.severity === 'medium' ? 'MEDEL' : 'LÅG';
                        doc.text(severityText, margin + 3, yPos + 5);
                        
                        doc.setTextColor(0);
                        doc.text(`${index + 1}. ${finding.title || finding.type || 'Fynd'}`, margin + 20, yPos + 5);
                        yPos += 8;
                        
                        if (finding.type) {
                          doc.setFontSize(10);
                          doc.setFont('helvetica', 'normal');
                          doc.setTextColor(75);
                          doc.text(`Typ: ${finding.type}`, margin + 3, yPos);
                          yPos += 5;
                        }
                        
                        if (finding.description) {
                          doc.setFontSize(10);
                          doc.setTextColor(50);
                          const descLines = doc.splitTextToSize(finding.description, pageWidth - 2 * margin - 3);
                          descLines.forEach((line: string) => {
                            if (yPos > pageHeight - margin) {
                              doc.addPage();
                              yPos = margin;
                            }
                            doc.text(line, margin + 3, yPos);
                            yPos += 4;
                          });
                        }
                        
                        yPos += 8;
                      });
                    }
                    
                    // Save PDF
                    const filename = `revision-${summary.company_name || currentReport.revision_id.slice(0, 8)}.pdf`;
                    doc.save(filename);

                    toast({
                      title: "Export lyckades",
                      description: "PDF-rapport har laddats ner",
                    });

                  } catch (error) {
                    console.error('PDF export error:', error);
                    toast({
                      title: "Export misslyckades",
                      description: "Kunde inte generera PDF-rapport",
                      variant: "destructive"
                    });
                  }
                }}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Exportera PDF
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {currentReport && (() => {
              const reportJson = currentReport.report_json || {};
              const allFindings = reportJson.findings || [];
              const unboosted = allFindings.filter((f: any) => 
                !f.details?.original_description && 
                f.description && 
                f.description.length < 120
              );
              
              if (unboosted.length > 10) {
                return (
                  <Alert className="mb-4">
                    <FileWarning className="h-4 w-4" />
                    <AlertTitle>Många findings är inte AI-förbättrade</AlertTitle>
                    <AlertDescription>
                      Endast {allFindings.length - unboosted.length} av {allFindings.length} findings har förbättrade beskrivningar. 
                      Klicka "Boost All Remaining" ovan för att förbättra resterande {unboosted.length} findings (tar ~{Math.ceil(unboosted.length * 8 / 60)} minuter).
                    </AlertDescription>
                  </Alert>
                );
              }
              return null;
            })()}
            <div className="mt-4">
              <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                <FileText className="h-5 w-5" />
                AI-genererad Rapport
              </h3>
              <iframe
                srcDoc={dummyReport}
                className="w-full h-[600px] border rounded"
                title="AI Report"
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
